import React, { useEffect } from 'react';

import { Controller, useForm } from 'react-hook-form';
import dayjs from '~/timeInit';

import { History as HistoryIcon, Search as SearchIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  GridLegacy as Grid,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DateRangePicker } from '@mui/x-date-pickers-pro';

import LocationAutocomplete from '~/components/PriceParrot/LocationAutocomplete';

import { COUNTRIES } from '~/consts/priceParrot';

import { Dayjs, formatDateISO } from '~/services/TimeService';

import { StayInputOverrides } from './otaParity/types';
import { SubmitScrapeInput } from './useOtaRateShopOrchestration';
import type { RateShopInputs } from './useScrapeSession';

type ScrapeFormInput = {
  locationUrl: string;
  dateRange: [Dayjs, Dayjs];
  countryCode: string;
};

const TODAY = dayjs();

const DEFAULT_START = TODAY;
const DEFAULT_END = TODAY.add(6, 'month');

function toFormValues(inputs: RateShopInputs | null | undefined): ScrapeFormInput {
  return {
    locationUrl: inputs?.locationUrl ?? '',
    dateRange: [
      inputs?.startDate ? dayjs(inputs.startDate) : DEFAULT_START,
      inputs?.endDate ? dayjs(inputs.endDate) : DEFAULT_END,
    ],
    countryCode: inputs?.countryCode ?? 'AU',
  };
}

interface Props {
  initialValues?: RateShopInputs | null;
  onChange: (values: RateShopInputs) => void;
  stayInputs: StayInputOverrides | null;
  isWorking: boolean;
  progressText: string;
  error: string | null;
  clearError: () => void;
  submitScrape: (input: SubmitScrapeInput) => Promise<void>;
  loadMostRecentJob: (locationUrl: string, currencyCode: string, countryCode: string) => Promise<void>;
}

export default function OtaRateShopForm({
  initialValues,
  onChange,
  stayInputs,
  isWorking,
  progressText,
  error,
  clearError,
  submitScrape,
  loadMostRecentJob,
}: Props) {
  const { control, handleSubmit, getValues, trigger, watch } = useForm<ScrapeFormInput>({
    defaultValues: toFormValues(initialValues),
  });

  const [locationUrl, [start, end], countryCode] = watch(['locationUrl', 'dateRange', 'countryCode']);
  const startDate = start?.isValid() ? formatDateISO(start.toDate()) : null;
  const endDate = end?.isValid() ? formatDateISO(end.toDate()) : null;
  useEffect(() => {
    onChange({ locationUrl, startDate, endDate, countryCode });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationUrl, startDate, endDate, countryCode]);

  const onFreshScrape = async (data: ScrapeFormInput) => {
    if (!stayInputs) {
      return;
    }
    const [start, end] = data.dateRange;
    await submitScrape({
      locationUrl: data.locationUrl,
      nights: stayInputs.nights,
      startDate: formatDateISO(start.toDate()),
      endDate: formatDateISO(end.toDate()),
      currencyCode: stayInputs.currencyCode,
      countryCode: data.countryCode,
    });
  };

  const onLoadLatest = async () => {
    if (!stayInputs) {
      return;
    }
    if (!(await trigger('locationUrl'))) {
      return;
    }
    const { locationUrl, countryCode } = getValues();
    await loadMostRecentJob(locationUrl, stayInputs.currencyCode, countryCode);
  };

  return (
    <Box>
      <form onSubmit={handleSubmit(onFreshScrape)}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <LocationAutocomplete
              control={control}
              name="locationUrl"
              label="Property"
              rules={{ required: 'Required' }}
            />
          </Grid>
          <Grid item xs={8} md={4}>
            <Controller
              name="dateRange"
              control={control}
              render={({ field }) => (
                <DateRangePicker {...field} localeText={{ start: 'Start date', end: 'End date' }} />
              )}
            />
          </Grid>
          <Grid item xs={4} md={2}>
            <Controller
              name="countryCode"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="Country" select fullWidth>
                  {COUNTRIES.map((c) => (
                    <MenuItem key={c.code} value={c.code}>
                      {c.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Stack direction="row" spacing={1.5}>
              <Button
                variant="outlined"
                startIcon={<HistoryIcon />}
                onClick={onLoadLatest}
                disabled={isWorking || !stayInputs}
              >
                Use Latest
              </Button>
              <Button type="submit" variant="contained" startIcon={<SearchIcon />} disabled={isWorking || !stayInputs}>
                Fresh Scrape
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </form>

      {isWorking && (
        <Box sx={{ mt: 3 }}>
          <LinearProgress />
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            {progressText}
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={clearError}>
          {error}
        </Alert>
      )}
    </Box>
  );
}
